export default {
  namespaced: true,
  
  state: {
    cache: {},
    loaded: false,
    preferenceMetadata: {
      mask_phi: {
        title: 'Mask PHI',
        description: 'Privacy setting for patient data',
        emptyMessage: 'Disabled'
      },
      table: {
        title: 'Table Rows',
        description: 'Default rows per page in tables',
        emptyMessage: 'Using default (10 rows)'
      },
      patient_overview: {
        title: 'Patient Overview',
        description: 'Field visibility and order settings',
        emptyMessage: 'No field preferences saved'
      },
      live_dashboard: {
        title: 'Live Dashboard',
        description: 'Dashboard column configuration',
        emptyMessage: 'No dashboard settings saved'
      },
      my_assignments_dashboard: {
        title: 'My Assignments Dashboard',
        description: 'Dashboard columns on My Assignments page',
        emptyMessage: 'No dashboard columns configured'
      },
      my_assignments_layout: {
        title: 'My Assignments Layout',
        description: 'Section order on My Assignments page',
        emptyMessage: 'Using default section order'
      },
      my_assignments_filters: {
        title: 'My Assignments Filters',
        description: 'Dashboard filter state on My Assignments page',
        emptyMessage: 'Using default filters'
      },
      infinity_watch: {
        title: 'Infinity Watch',
        description: 'Carousel rotation timer',
        emptyMessage: 'Using default timer (60s)'
      },
      time_machine: {
        title: 'Time Machine',
        description: 'Visible time machine categories',
        emptyMessage: 'All categories visible by default'
      },
      quickview: {
        title: 'QuickView',
        description: 'Widget order and visibility',
        emptyMessage: 'Using default widget order'
      },
      videoVisitSidebar: {
        title: 'Video Visit Sidebar',
        description: 'Widget order and visibility for video visit sidebar',
        emptyMessage: 'Using default widget order'
      },
      stretch_pages: {
        title: 'Stretch Pages',
        description: 'Whether pages stretch to full width',
        emptyMessage: 'Using auto width'
      },
      video_meeting: {
        title: 'Video Meeting',
        description: 'Default video meeting settings',
        emptyMessage: 'Using default video meeting settings'
      },
      patient_form_sections: {
        title: 'Patient Form Sections',
        description: 'Expanded/collapsed state of patient form sections',
        emptyMessage: 'Using default section states'
      },
      custom_field_filters: {
        title: 'Custom Field Filters',
        description: 'Show or hide non-default custom fields in filter dropdowns',
        emptyMessage: 'Hidden by default'
      },
      theme: {
        title: 'Theme',
        description: 'Light, dark, or auto (follows system)',
        emptyMessage: 'Auto (follows system)'
      }
    }
  },
  
  mutations: {
    SET_CACHE(state, { key, value }) {
      state.cache[key] = value
    },
    
    SET_ALL_CACHE(state, preferences) {
      preferences.forEach(pref => {
        state.cache[pref.key] = pref.settings
      })
      state.loaded = true
    },
    
    CLEAR_CACHE(state, key) {
      if (key) {
        delete state.cache[key]
      } else {
        state.cache = {}
      }
    }
  },
  
  actions: {
    async initialize({ state, dispatch }) {
      if (!state.loaded) {
        await dispatch('loadAll')
      }
    },
    
    async loadAll({ commit }) {
      const response = await this.$http.get('/user_preferences')
      commit('SET_ALL_CACHE', response.data)
      return response.data
    },
    
    async get({ state, commit }, { key, subKey }) {
      if (state.cache[key] !== undefined) {
        const value = state.cache[key]
        return subKey ? value[subKey] : value
      }

      try {
        const response = await this.$http.get(`/user_preferences/${key}`)
        const settings = response.data.settings || {}
        commit('SET_CACHE', { key, value: settings })
        return subKey ? settings[subKey] : settings
      } catch (error) {
        if (error.response?.status === 404) {
          commit('SET_CACHE', { key, value: {} })
          return subKey ? undefined : {}
        }
        throw error
      }
    },
    
    async set({ state, commit }, { key, value, subKey }) {
      let settings = state.cache[key] || {}
      
      if (subKey) {
        settings = { ...settings, [subKey]: value }
      } else {
        settings = value
      }
      
      commit('SET_CACHE', { key, value: settings })
      
      await this.$http.put(`/user_preferences/${key}`, { 
        user_preference: { settings }
      }, { skipLoading: true })
    },
    
    async clear({ commit }, key) {
      if (key) {
        await this.$http.delete(`/user_preferences/${key}`)
        commit('CLEAR_CACHE', key)
      } else {
        await this.$http.delete('/user_preferences/delete_all')
        commit('CLEAR_CACHE')
      }
    }
  },
  
  getters: {
    getPreference: (state) => (key, subKey) => {
      const cached = state.cache[key]
      if (!cached) return null
      return subKey ? cached[subKey] : cached
    },
    
    allPreferences: (state) => {
      const prefs = []
      
      Object.entries(state.preferenceMetadata).forEach(([key, metadata]) => {
        const settings = state.cache[key] || {}
        const hasData = Object.keys(settings).length > 0
        
        let description = metadata.emptyMessage
        
        if (hasData) {
          switch(key) {
            case 'mask_phi':
              description = settings.enabled ? 'Enabled' : 'Disabled'
              break;
            case 'table':
              description = `${settings.per_page} rows per page`
              break;
            case 'patient_overview':
              description = `${(settings.visible_columns || []).length} columns visible`
              break;
            case 'live_dashboard':
              description = `${(settings.enabled_columns || []).length} columns enabled`
              break;
            case 'my_assignments_dashboard':
              description = `${(settings.enabled_columns || []).length} columns enabled`
              break;
            case 'my_assignments_layout':
              description = `Custom section order`
              break;
            case 'my_assignments_filters':
              description = settings.my_assignments_only ? 'My Assignments Only enabled' : 'Using default filters'
              break;
            case 'infinity_watch':
              description = `Timer set to ${settings.carousel_duration || 60} seconds`
              break;
            case 'time_machine':
              description = `${(settings.visible_groups || []).length} groups selected`
              break;
            case 'quickview':
              description = `Custom widgets order`
              break;
            case 'videoVisitSidebar':
              description = `Custom widgets order`
              break;
            case 'stretch_pages':
              description = settings.enabled ? 'Pages stretch to 100%' : 'Using auto width'
              break;
            case 'video_meeting':
              description = `Custom video meeting settings saved`
              break;
            case 'patient_form_sections':
              description = ''
              break;
            case 'custom_field_filters':
              description = settings.show_all ? 'Showing all custom fields' : 'Hidden by default'
              break;
            case 'theme':
              description = settings.mode === 'dark' ? 'Dark' : settings.mode === 'light' ? 'Light' : 'Auto (System)'
              break;
          }
        }
        
        prefs.push({
          key,
          title: metadata.title,
          subtitle: metadata.description,
          description,
          hasData
        })
      })
      
      return prefs
    },
    
    hasAnyPreferences: (state) => {
      return Object.keys(state.cache).some(key => 
        Object.keys(state.cache[key] || {}).length > 0
      )
    }
  }
} 